<script setup lang="ts">
import { d2s, GET } from "../data/api.ts";
import type { Booking, Person } from "../data/types";
import { computed, onMounted, ref } from "vue";
import { usePeopleStore, useToastStore } from "../data/store.ts";
import { holidays } from "../data/holidays.ts";
import { deskLookup, desks } from "../data/rooms.ts";

const toaster = useToastStore();
const peopleStore = usePeopleStore();

const LOOK_BACK = 2;
const FUTURE_STEPS = 21;
const NUM_DAYS = FUTURE_STEPS;

const props = defineProps<{ people: string[]; highlight: string | null }>();
const bookings = ref<Booking[]>([]);

const startDate = new Date();
const endDate = new Date();
startDate.setDate(startDate.getDate() - LOOK_BACK);
endDate.setDate(endDate.getDate() + FUTURE_STEPS);

onMounted(() => {
  GET<Booking[]>("/bookings/", { start: d2s(startDate), end: d2s(endDate) })
    .then((bookings_) => {
      bookings.value = bookings_.map((b) => ({
        ...b,
        day: new Date(b.day),
      }));
    })
    .catch(() => {
      toaster.toast("Failed to load bookings", "WARN");
    });
});

const panel = computed(() => {
  const desks = new Set<number>();
  const rooms = new Set<number>();

  // @ts-ignore
  const ret = Object.fromEntries(
    [...Array(NUM_DAYS)].map(
      (
        //@ts-ignore
        v,
        i,
      ) => {
        const now = new Date();
        const day = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + i - 1,
        );
        return [
          day.toDateString(),
          {
            day: day,
            isWeekday: (day.getDay() + 6) % 7 <= 4,
            isToday: day.toDateString() == now.toDateString(),
            isHoliday: !!holidays[day.toDateString()],
            isMonday: day.getDay() === 1,
            holiday: holidays[day.toDateString()]?.name,
            desks: Object.fromEntries(
              Object.keys(deskLookup).map((key) => [
                key,
                {
                  acronyms: [] as Person[],
                  bookings: [] as Booking[],
                },
              ]),
            ),
          },
        ];
      },
    ),
  );

  // add bookings to table
  bookings.value.forEach((booking) => {
    if (
      props.people.indexOf(booking.person) >= 0 &&
      booking.type !== "FREE" &&
      booking.type !== "MEETING"
    ) {
      desks.add(booking.desk);
      rooms.add(deskLookup[booking.desk].roomIdx);
      ret[booking.day.toDateString()]?.desks[booking.desk]?.bookings.push(
        booking,
      );
    }
  });

  return {
    desks,
    rooms,
    table: ret,
  };
});
</script>

<template>
  <table class="desks">
    <thead>
      <tr>
        <th></th>
        <template v-for="column in panel.table" :key="column.day">
          <th
            v-if="column.isWeekday"
            :class="{
              'col-weekend-day': !column.isWeekday,
              'col-today': column.isToday,
              'col-weekday': column.isWeekday,
              'col-holiday': column.isHoliday,
              'col-monday': column.isMonday,
            }"
          >
            <div class="colhead">
              <div class="date-day">
                {{
                  column.day
                    .toLocaleString("default", { weekday: "short" })
                    .slice(0, 2)
                }}
              </div>
              <div class="date-text">
                {{ column.day.getDate() }}.&nbsp;{{
                  column.day.toLocaleString("default", { month: "short" })
                }}
              </div>
            </div>
          </th>
        </template>
      </tr>
    </thead>

    <template v-for="room in panel.rooms" :key="room">
      <tbody>
        <template v-for="(desk, i) in desks[room].desks" :key="desk.id">
          <tr v-if="panel.desks.has(desk.id)">
            <th class="desk">
              <span class="border-end me-2 pe-2">{{ desks[room].name }}</span>
              <span class="text-muted me-2">{{ desk.name }}</span>
            </th>
            <template v-for="column in panel.table" :key="+column.day">
              <td
                v-if="column.isWeekday"
                :class="{
                  'col-weekend-day': !column.isWeekday,
                  'col-today': column.isToday,
                  'col-weekday': column.isWeekday,
                  'col-holiday': column.isHoliday,
                  'col-monday': column.isMonday,
                  'first-in-room': i === 0,
                  'last-in-room': i === desks[room].desks.length - 1,
                }"
                class="desk-cell small text-muted"
              >
                <div
                  v-for="book in column.desks[desk.id]?.bookings"
                  :key="book.id"
                  class=""
                  :class="{ high: book.person === highlight }"
                >
                  {{ peopleStore.people?.[book.person]?.acro }}
                </div>
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </template>
  </table>
</template>

<style scoped>
.desks {
  --width-room-col: 1.5em;
  --width-desk-col: 1.5em;

  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid black;
  margin-left: auto;
  margin-right: auto;

  .col-today > div {
    border: 0;
  }

  .col-today {
    border-left: var(--today-border-width) dashed red;
    border-right: var(--today-border-width) dashed red;
  }

  .desk-cell {
    height: 2rem;
    line-height: 2rem;
    vertical-align: middle;
  }

  div.high {
    background-color: transparent;
  }

  .desk-cell:has(div.high) {
    background-color: var(--bs-primary-bg-subtle);
  }

  table {
    border-collapse: collapse;
  }

  tbody::after {
    content: "";
    display: table-row;
    height: 0.25em;
  }

  thead th,
  tbody th {
    font-weight: inherit;
    font-size: inherit;
  }

  thead th {
    background-color: var(--bg-weekday);
    height: 0;
  }

  tbody tr:first-of-type {
    border-top: 1px solid black;
  }

  tbody tr:last-of-type {
    border-bottom: 1px solid black;
    margin-bottom: 0.25em;
  }

  td {
    padding: 0;
    margin: 0;
  }
}
</style>
